//
// Copyright (c) 2011-2024 by Curalate, Inc
//

import React, { useState } from 'react';
import styled from 'styled-components';

import Icon from '@components/atoms/icon/Icon';
import LabeledCheckbox from '@components/atoms/labeled-checkbox/LabeledCheckbox';
import { FilterOption } from '@components/media-filters/filterSectionModels';
import { DropdownMenu } from '@components/primitives/dropdown-menu/DropdownMenu';

export interface CheckboxSetSubFilterProps {
  isDisabled?: boolean;
  label: string;
  options: FilterOption<string>[];
  onChange: (section: FilterOption<string>) => void;
  onChangeAll: (value: boolean) => void;
}

export const CheckboxSetSubFilter = ({
  isDisabled,
  label,
  options,
  onChange,
  onChangeAll,
}: CheckboxSetSubFilterProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const areAllSelected = options.every((option) => option.isSelected);
  const areSomeSelected = options.some((option) => option.isSelected);

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownTrigger
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={() => setIsOpen(false)}
      >
        <Checkbox
          isChecked={areAllSelected}
          isIndeterminate={areSomeSelected && !areAllSelected}
          handleClick={onChangeAll}
          label={label}
        />
        <Icon name='chevron-right' />
      </DropdownTrigger>

      <DropdownContent
        align='start'
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={() => setIsOpen(false)}
        side='right'
        sideOffset={0}
      >
        <CheckboxSet>
          {options.map((option) => (
            <Checkbox
              handleClick={(isSelected) => onChange({ ...option, isSelected })}
              isChecked={option.isSelected}
              isDisabled={isDisabled}
              key={option.label}
              value={option.value}
              label={option.label}
            />
          ))}
        </CheckboxSet>
      </DropdownContent>
    </DropdownMenu>
  );
};

const DropdownContent = styled(DropdownMenu.Content)(
  ({ theme }) => `
  padding: ${theme.spacings[3]};
`,
);

// Using a `div` instead of `DropdownMenu.Item` to avoid interference with the labeled checkbox's behavior.
// `DropdownMenu.Item` is intended for dropdown single selection items, which conflicts with the checkbox functionality.
const CheckboxSet = styled.div(
  ({ theme }) => `
    display: flex;
    flex-direction: column;
    gap: ${theme.spacings[3]};
    width: 100%;
`,
);

const Checkbox = styled(LabeledCheckbox)(
  ({ theme }) => `
    width: 100%;
    padding: ${theme.spacings[0]};

    label {
      width: 100%;
    }
`,
);

const DropdownTrigger = styled(DropdownMenu.Trigger)(
  ({ theme }) => `
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: inherit;
    outline: unset;
    border: unset;
    padding: ${theme.spacings[0]};
    pointer-events: auto;
`,
);
